import React, { useCallback, useEffect, useState } from "react";
import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    Clock,
    Mail,
    RefreshCw,
    Server,
    Signal,
    Wifi,
    WifiOff,
    Zap,
    MessageCircle,
    Send,
    RotateCcw,
    Download,
    Play,
} from "lucide-react";
import { toast } from "sonner";
import { whatsappMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";

const REFRESH_MS = 30000;

function MetricCard({ label, value, sub, accent = "emerald", icon: Icon }) {
    const borders = {
        emerald: "border-emerald-500/25 bg-emerald-500/5",
        rose: "border-rose-500/25 bg-rose-500/5",
        amber: "border-amber-500/25 bg-amber-500/5",
        sky: "border-sky-500/25 bg-sky-500/5",
        violet: "border-violet-500/25 bg-violet-500/5",
    };
    const values = {
        emerald: "text-emerald-400",
        rose: "text-rose-400",
        amber: "text-amber-400",
        sky: "text-sky-400",
        violet: "text-violet-400",
    };
    return (
        <div className={`rounded-xl border p-4 ${borders[accent] || borders.emerald}`}>
            <div className="flex items-start justify-between gap-2">
                <p className="text-[10px] uppercase tracking-wider text-[#8b93ae] font-medium">{label}</p>
                {Icon && <Icon className={`h-4 w-4 shrink-0 ${values[accent]}`} />}
            </div>
            <p className={`text-2xl font-display font-bold mt-2 ${values[accent]}`}>{value}</p>
            {sub && <p className="text-xs text-[#8b93ae] mt-1">{sub}</p>}
        </div>
    );
}

function StatusPill({ ok, label }) {
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold border ${
                ok
                    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                    : "bg-rose-500/10 text-rose-300 border-rose-500/30"
            }`}
        >
            {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
            {label}
        </span>
    );
}

function TrendChart({ series = [] }) {
    const max = Math.max(...series.map((d) => d.gonderildi || 0), 1);
    return (
        <div className="flex items-end gap-2 h-24 pt-2">
            {series.map((d) => {
                const h = Math.round((100 * (d.gonderildi || 0)) / max);
                return (
                    <div key={d.tarih} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                        <div className="w-full flex items-end justify-center h-16 bg-black/20 rounded-t-md">
                            <div
                                className="w-[70%] rounded-t bg-gradient-to-t from-[#1da851] to-[#25d366] transition-all"
                                style={{ height: `${Math.max(h, 4)}%` }}
                            />
                        </div>
                        <span className="text-[9px] text-[#8b93ae] truncate w-full text-center">
                            {d.tarih?.slice(-5)}
                        </span>
                        <span className="text-[10px] text-emerald-400 font-mono">{d.gonderildi}</span>
                    </div>
                );
            })}
        </div>
    );
}

export default function WhatsAppUltraDashboard({ runBusy, busyKey }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await whatsappMerkez.dashboard();
            setData(res);
            setLastRefresh(new Date());
        } catch {
            if (!silent) toast.error("Komuta merkezi verisi yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(() => load(true), REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    const conn = data?.baglanti || {};
    const saglik = data?.saglik || {};
    const bugun = data?.bugun || {};
    const hafta = data?.hafta || {};
    const bekleyen = data?.bekleyen || {};
    const ayar = data?.ayar_ozet || {};
    const connOk = conn.connected && conn.state === "open";

    async function handleKuyruk() {
        await runBusy("komuta-kuyruk", async () => {
            const res = await whatsappMerkez.kuyrukIsle(30);
            toast.success(`Kuyruk işlendi: ${res.islenen ?? res.processed ?? "OK"}`);
            load(true);
        });
    }

    async function handleRetry() {
        await runBusy("komuta-retry", async () => {
            const res = await whatsappMerkez.retryHatalar(25);
            toast.success(`Yeniden deneme: ${res.retried ?? 0} kayıt`);
            load(true);
        });
    }

    async function handleRapor() {
        await runBusy("komuta-rapor", async () => {
            const res = await whatsappMerkez.raporGonder();
            if (res.ok) toast.success(`Rapor gönderildi: ${(res.recipients || []).join(", ")}`);
            else toast.error(res.detail || "Rapor gönderilemedi.");
        });
    }

    if (loading && !data) {
        return (
            <div className="rounded-xl border border-white/10 bg-[#0b1437]/80 p-8 text-center text-[#8b93ae]">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-3 text-[#25d366]" />
                Komuta merkezi yükleniyor…
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Hero — Bağlantı */}
            <section
                className={`rounded-2xl border p-5 relative overflow-hidden ${
                    connOk
                        ? "border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-[#0b1437] to-[#0b1437]"
                        : "border-rose-500/30 bg-gradient-to-br from-rose-950/40 via-[#0b1437] to-[#0b1437]"
                }`}
            >
                <div className="absolute top-0 right-0 w-48 h-48 bg-[#25d366]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div
                            className={`p-3 rounded-xl border ${
                                connOk ? "bg-emerald-500/15 border-emerald-500/30" : "bg-rose-500/15 border-rose-500/30"
                            }`}
                        >
                            {connOk ? (
                                <Wifi className="h-7 w-7 text-emerald-400" />
                            ) : (
                                <WifiOff className="h-7 w-7 text-rose-400" />
                            )}
                        </div>
                        <div>
                            <h2 className="text-lg font-display font-bold text-[#f5ecd6] flex items-center gap-2">
                                WhatsApp Komuta Merkezi
                                {connOk && (
                                    <span className="relative flex h-2.5 w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                                    </span>
                                )}
                            </h2>
                            <p className="text-sm text-[#8b93ae] mt-1">
                                Instance <span className="text-[#c5cad8] font-mono">{conn.instance || ayar.instance || "mbs"}</span>
                                {conn.profile_name && (
                                    <> · <span className="text-[#c5cad8]">{conn.profile_name}</span></>
                                )}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <StatusPill ok={connOk} label={connOk ? "Evolution Açık" : `Durum: ${conn.state || "kapalı"}`} />
                                <StatusPill ok={ayar.global_aktif} label={ayar.global_aktif ? "Gönderim aktif" : "Gönderim kapalı"} />
                                <StatusPill ok={ayar.otomatik_gonderim} label={ayar.otomatik_gonderim ? "Otomatik kuyruk" : "Manuel kuyruk"} />
                                {ayar.test_modu && (
                                    <span className="text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                                        Test modu
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => load()} className="text-xs h-8">
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            Yenile
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleKuyruk} className="text-xs h-8">
                            <Play className="h-3.5 w-3.5 mr-1" />
                            Kuyruk işle
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleRetry} className="text-xs h-8">
                            <RotateCcw className="h-3.5 w-3.5 mr-1" />
                            Hataları dene
                        </Button>
                        <Button size="sm" onClick={handleRapor} className="text-xs h-8 bg-violet-600 hover:bg-violet-500 text-white">
                            <Mail className="h-3.5 w-3.5 mr-1" />
                            Rapor gönder
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => whatsappMerkez.raporPdfIndir()}
                            className="text-xs h-8"
                        >
                            <Download className="h-3.5 w-3.5 mr-1" />
                            PDF
                        </Button>
                    </div>
                </div>
                {lastRefresh && (
                    <p className="text-[10px] text-[#8b93ae] mt-4 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Son güncelleme: {lastRefresh.toLocaleTimeString("tr-TR")} · Otomatik {REFRESH_MS / 1000}s
                    </p>
                )}
            </section>

            {/* Bugün + Hafta metrikleri */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <MetricCard label="Bugün gönderildi" value={bugun.gonderildi ?? 0} sub={`Toplam ${bugun.toplam ?? 0}`} accent="emerald" icon={Send} />
                <MetricCard label="Bugün hata" value={bugun.hata ?? 0} sub={`Başarı %${bugun.basari_orani ?? 0}`} accent="rose" icon={AlertTriangle} />
                <MetricCard label="Bekleyen" value={bekleyen.bekleyen_toplam ?? bugun.bekleyen ?? 0} sub={`Onay: ${bekleyen.onay_bekliyor ?? 0}`} accent="amber" icon={Clock} />
                <MetricCard label="Hafta gönderildi" value={hafta.gonderildi ?? 0} sub={`Hata ${hafta.hata ?? 0}`} accent="sky" icon={MessageCircle} />
                <MetricCard label="Inbox bugün" value={bugun.inbox_gelen ?? 0} sub={`Giden ${bugun.inbox_giden ?? 0}`} accent="violet" icon={MessageCircle} />
                <MetricCard label="Okunmamış" value={saglik.inbox_okunmamis ?? 0} sub="Gelen kutusu" accent="amber" icon={MessageCircle} />
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                {/* Trend */}
                <section className="lg:col-span-2 rounded-xl border border-white/10 bg-[#0b1437]/80 p-4">
                    <h3 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2 mb-3">
                        <Activity className="h-4 w-4 text-[#25d366]" />
                        7 günlük gönderim trendi
                    </h3>
                    <TrendChart series={data?.gunluk_seri || []} />
                    <div className="mt-3 flex gap-4 text-xs text-[#8b93ae]">
                        <span>Teslim: <strong className="text-sky-400">{hafta.teslim_edildi ?? 0}</strong></span>
                        <span>Okundu: <strong className="text-violet-400">{hafta.okundu ?? 0}</strong></span>
                        <span>Hafta başarı: <strong className="text-emerald-400">%{hafta.basari_orani ?? 0}</strong></span>
                    </div>
                </section>

                {/* Sistem sağlık */}
                <section className="rounded-xl border border-emerald-500/20 bg-[#0b1437]/80 p-4">
                    <h3 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2 mb-3">
                        <Server className="h-4 w-4 text-emerald-400" />
                        Sistem sağlığı
                    </h3>
                    <div className="space-y-2 text-sm">
                        {[
                            ["Daphne", saglik.daphne, saglik.daphne === "active"],
                            ["Celery worker", saglik.celery, saglik.celery === "active"],
                            ["Celery beat", saglik.celery_beat, saglik.celery_beat === "active"],
                            ["Evolution Docker", saglik.evolution_docker, saglik.evolution_docker === "running"],
                            ["API yanıt", saglik.api_yanit_ms >= 0 ? `${saglik.api_yanit_ms} ms` : "Hata", saglik.api_yanit_ms >= 0],
                            ["Disk", `%${saglik.disk_root_yuzde} · ${saglik.disk_root_gb_bos} GB boş`, saglik.disk_root_yuzde < 85],
                            ["Kuyruk", saglik.kuyruk_bekleyen, saglik.kuyruk_bekleyen < 50],
                            ["Rate limit", `${saglik.rate_limit_dakika}/dk`, true],
                        ].map(([k, v, ok]) => (
                            <div key={k} className="flex justify-between items-center py-1.5 border-b border-white/5">
                                <span className="text-[#8b93ae] text-xs">{k}</span>
                                <span className={`text-xs font-medium ${ok ? "text-[#c5cad8]" : "text-rose-400"}`}>{v ?? "—"}</span>
                            </div>
                        ))}
                    </div>
                    {data?.rapor_email?.length > 0 && (
                        <p className="mt-3 text-[10px] text-[#8b93ae]">
                            Haftalık rapor: {data.rapor_email.join(", ")}
                        </p>
                    )}
                </section>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                {/* Son hatalar */}
                <section className="rounded-xl border border-rose-500/20 bg-[#0b1437]/80 p-4">
                    <h3 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2 mb-3">
                        <AlertTriangle className="h-4 w-4 text-rose-400" />
                        Son hatalar
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {(data?.son_hatalar || []).length === 0 ? (
                            <p className="text-sm text-[#8b93ae]">Son hata kaydı yok.</p>
                        ) : (
                            data.son_hatalar.map((h) => (
                                <div key={h.id} className="rounded-lg border border-rose-500/15 bg-rose-500/5 p-3 text-xs">
                                    <div className="flex justify-between gap-2">
                                        <span className="text-[#f5ecd6] font-medium truncate">
                                            {h.muvekkil || h.telefon}
                                        </span>
                                        <span className="text-[#8b93ae] shrink-0">{h.zaman?.slice(0, 16)}</span>
                                    </div>
                                    <p className="text-rose-300/90 mt-1 line-clamp-2">{h.hata_mesaji}</p>
                                    <p className="text-[#8b93ae] mt-1">
                                        {h.sablon_kod} · Deneme {h.deneme_sayisi}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                {/* Son aktivite */}
                <section className="rounded-xl border border-white/10 bg-[#0b1437]/80 p-4">
                    <h3 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2 mb-3">
                        <Signal className="h-4 w-4 text-sky-400" />
                        Canlı aktivite
                    </h3>
                    <div className="space-y-1 max-h-64 overflow-y-auto">
                        {(data?.son_aktivite || []).map((a) => (
                            <div
                                key={a.id}
                                className="flex items-center justify-between gap-2 py-2 border-b border-white/5 text-xs"
                            >
                                <div className="min-w-0">
                                    <span className="text-[#f5ecd6] truncate block">
                                        {a.muvekkil || a.telefon}
                                    </span>
                                    <span className="text-[#8b93ae]">{a.sablon_kod}</span>
                                </div>
                                <div className="text-right shrink-0">
                                    <span
                                        className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                            a.durum === "GONDERILDI"
                                                ? "bg-emerald-500/10 text-emerald-300"
                                                : a.durum === "HATA"
                                                  ? "bg-rose-500/10 text-rose-300"
                                                  : "bg-amber-500/10 text-amber-300"
                                        }`}
                                    >
                                        {a.durum_display}
                                    </span>
                                    <p className="text-[#8b93ae] mt-0.5">{a.zaman?.slice(11, 16)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            {/* Otomasyon özeti */}
            <section className="rounded-xl border border-white/10 bg-[#0b1437]/80 p-4">
                <h3 className="text-sm font-semibold text-[#f5ecd6] flex items-center gap-2 mb-3">
                    <Zap className="h-4 w-4 text-amber-400" />
                    Otomasyon durumu
                </h3>
                <div className="flex flex-wrap gap-2">
                    {[
                        ["Günlük cron", ayar.cron_aktif, `${ayar.cron_saat}:00`],
                        ["Zincir mesaj", ayar.zincir_aktif],
                        ["Onay modu", ayar.onay_modu],
                        ["Inbox bot", ayar.inbox_bot],
                    ].map(([label, active, extra]) => (
                        <span
                            key={label}
                            className={`text-xs px-3 py-1.5 rounded-lg border ${
                                active
                                    ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300"
                                    : "border-white/10 bg-white/5 text-[#8b93ae]"
                            }`}
                        >
                            {label}{extra ? ` (${extra})` : ""}: {active ? "Açık" : "Kapalı"}
                        </span>
                    ))}
                </div>
                <p className="text-xs text-[#8b93ae] mt-3">
                    Tüm zaman: {data?.tum_zaman?.gonderildi ?? 0} gönderildi · {data?.tum_zaman?.hata ?? 0} hata ·{" "}
                    {data?.tum_zaman?.iptal ?? 0} iptal
                </p>
            </section>
        </div>
    );
}
